import {
  createRealtimeHook,
  MockNatsAdapter,
  type RealtimePayload,
  type RealtimeMessageHandler,
} from "@web-monorepo/realtime";
import { showErrorBanner } from "@classdojo/web/pods/errorHandling/components/errorBannerApi";
import { ErrorBannerValues } from "@classdojo/web/pods/errorHandling/errorBanner";
import { isTesting } from "#/src/utils/env";
import { metrics } from "#/src/utils/metrics";

export const mockAdapter = isTesting ? new MockNatsAdapter() : null;

const serverUrl = `wss://${window.location.host}/nats`;

const showRealtimeSyncBanner = () => showErrorBanner(ErrorBannerValues.realtimeSync);

const {
  useRealtime: useRealtimeBase,
  publish,
  init,
  close,
  addReconnectListener,
} = createRealtimeHook({
  serverUrl,
  name: "web-marketplace",
  mockAdapter,
  metrics,
  // Subscription auth errors (PermissionViolation, etc.) have no automatic recovery,
  // so surface them to the user immediately. Connection-level events
  // (onConnectionExhausted, onInitError) intentionally have no callback — the hook
  // schedules its own recovery rounds; only onRecoveryExhausted means we've truly given up.
  //
  // Neither the post-exhaustion state machine (web_nats_exhausted_state_machine) nor the E1
  // poll (web_nats_exhausted_poll) is wired here: marketplace/tutor "no realtime" is dominated
  // by a separate server-side auth issue (deterministic auth-callout rejection) that slow
  // re-dials can never recover, so the machine would add pointless retry load to the auth
  // subsystem under investigation for near-zero benefit — and the poll is inert here anyway
  // (marketplace registers no reconnect listeners). Revisit both once that auth issue resolves.
  onError: showRealtimeSyncBanner,
  onRecoveryExhausted: showRealtimeSyncBanner,
});

export { publish, init, close, addReconnectListener };

/**
 * Subscribe to marketplace realtime messages scoped to a single entity subject
 * (`user.{entityId}`).
 *
 * Pass `enabled: false` to tear down the subscription cleanly. `entityId` is the
 * id `RealtimeUserClient` uses to namespace the subscription — the marketplace
 * uid for SNTS, or the viewer's personProfileId for tutorDm messaging.
 */
export function useRealtime<T extends RealtimePayload["action"]>(
  { entityId, enabled }: { entityId: string | null | undefined; enabled: boolean },
  actions: T[],
  handler: RealtimeMessageHandler<Extract<RealtimePayload, { action: T }>>,
) {
  useRealtimeBase({ entityId: entityId ?? undefined, enabled }, actions, handler);
}
